import { Link } from 'react-router-dom'
import { SeaBackdrop } from '@/components/SeaBackdrop'
import { FeatureCard } from '@/components/FeatureCard'
import { PlayLink } from '@/components/PlayLink'
import { SeoHead } from '@/components/SeoHead'
import { ROUTE_SEO } from '@/seo'
import { BookIcon } from '@/components/game/GameIcons'
import { CodexExplorerFab } from '@/components/CodexExplorerFab'
import { SEASON_LENGTH_WEEKS } from '@/config'

/**
 * Public landing / homepage shown to guests at "/".
 *
 * A full-screen maritime hero (deep-ocean gradient, turning compass rose,
 * charted route, moonlit galleon on animated swell — all in <SeaBackdrop/>)
 * with a big PLAY NOW call-to-action, followed by four "ways to play" cards
 * and a trust line. Visuals are CSS + inline SVG only; copy is French, in the
 * "age of sail / treasure-chart" voice. Routing contract: PLAY NOW → /register,
 * secondary link → /login.
 */
export function Landing() {
  return (
    <>
      <SeoHead {...ROUTE_SEO['/']} />
      <style>{LANDING_CSS}</style>

      {/* ============================ HERO ============================ */}
      <section className="relative min-h-screen overflow-hidden flex flex-col">
        {/* shipAlign="side" : sur desktop le galion sort de sous le contenu
            centré (CTA) et navigue dans la marge droite, visible en entier ;
            sur mobile il reste centré (déjà OK), comme sur /login & /register. */}
        <SeaBackdrop shipAlign="side" />

        {/* secondary login link, top-right */}
        <div className="relative z-20 max-w-6xl w-full mx-auto px-6 pt-6 flex justify-end">
          <PlayLink path="/login" className="text-sm text-ocean-200 hover:text-sand-300 transition-colors">
            Déjà capitaine ?{' '}
            <span className="text-sand-300 underline underline-offset-4 decoration-sand-500/60">Se connecter</span>
          </PlayLink>
        </div>

        {/* hero content — pb-56 sur mobile (vs sm:pb-44 desktop, inchangé) remonte
            le bloc pour aérer entre le lien « Se connecter » et la pilule Codex flottante. */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pt-6 pb-56 sm:pb-44">
          {/* High-res brand mark — the global header is hidden on the landing, so
              the logo carries the brand here. SVG = crisp at any size; modest on
              mobile so the scroll hint below stays above the fold. */}
          <img
            src="/corsalia-logo.svg"
            alt=""
            className="w-20 h-20 sm:w-36 sm:h-36 mb-4 sm:mb-6 drop-shadow-[0_6px_28px_rgba(229,204,143,0.35)]"
          />
          <p className="flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-[0.34em] text-ocean-200 mb-6 sm:mb-7">
            <span className="h-px w-8 sm:w-12 bg-gradient-to-r from-transparent to-sand-500/70" />
            Stratégie maritime · asynchrone
            <span className="h-px w-8 sm:w-12 bg-gradient-to-l from-transparent to-sand-500/70" />
          </p>

          <h1 className="font-display font-bold ink-title leading-[0.92] tracking-tight text-[clamp(3.5rem,13vw,9.5rem)]">
            Corsalia
          </h1>

          <p className="mt-7 max-w-2xl text-base sm:text-xl text-ocean-100/90 leading-relaxed text-pretty">
            Un archipel. Quatre factions. Une saison pour bâtir ton empire, armer ta flotte et écrire ta légende,{' '}
            <span className="text-sand-200">à ton rythme</span>.
          </p>

          <div className="mt-11 flex flex-col items-center gap-5">
            <PlayLink
              path="/register"
              className="cta-glow group inline-flex items-center gap-3 whitespace-nowrap rounded-xl bg-gradient-to-b from-sand-300 to-sand-500 px-11 sm:px-14 py-4 sm:py-[1.1rem]
                         font-display text-2xl sm:text-[2rem] font-bold text-ocean-900 ring-1 ring-sand-100/60 transition-transform hover:-translate-y-0.5 active:translate-y-0"
            >
              <span className="grid place-items-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-ocean-900/15 ring-1 ring-ocean-900/20 transition-transform group-hover:scale-105">
                <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 translate-x-[1px]" fill="currentColor" aria-hidden="true">
                  <path d="M7 4.5v15l13-7.5z" />
                </svg>
              </span>
              Lève l'ancre !
            </PlayLink>
            <PlayLink path="/login" className="text-sm text-ocean-200 hover:text-sand-300 transition-colors">
              Déjà capitaine ?{' '}
              <span className="underline underline-offset-4 decoration-sand-500/60">Se connecter</span>
            </PlayLink>
            {/* Le repère de défilement vit désormais dans la pilule flottante
                <CodexExplorerFab/> (persiste jusqu'au bloc Codex). */}
          </div>
        </div>
      </section>

      {/* ============================ FEATURES ============================ */}
      <section id="features" className="relative bg-[#04161c] py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-500/40 to-transparent" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-ocean-300 mb-3">Le métier de capitaine</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sand-200">Quatre manières de marquer l'archipel</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} icon={f.icon} title={f.title} text={f.text} />
            ))}
          </div>

          {/* trust line */}
          <div className="mt-16 flex flex-col items-center gap-5">
            <div className="flex items-center gap-3 w-full max-w-md">
              <span className="flex-1 rope-rule" />
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-sand-500" fill="currentColor" aria-hidden="true">
                <path d="M12 2l2.4 6.5L21 9l-5 4 1.8 7L12 16.5 6.2 20 8 13 3 9l6.6-.5z" />
              </svg>
              <span className="flex-1 rope-rule" />
            </div>
            <p className="text-center text-xs sm:text-sm text-ocean-300 tracking-wide">
              Pas de pay-to-win <span className="text-sand-500/70 mx-1.5">·</span>
              Pas de temps réel imposé <span className="text-sand-500/70 mx-1.5">·</span>
              Saison de {SEASON_LENGTH_WEEKS} semaines, puis tout repart à zéro
            </p>
          </div>

          {/* Codex CTA — every rule of the archipelago, before you embark.
              #codex-cta = la cible du scroll + de l'observer de <CodexExplorerFab/>. */}
          <div id="codex-cta" className="mt-16 scroll-mt-28 flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-ocean-300">Toutes les règles, mécaniques et secrets de l'archipel t'attendent.</p>
            <Link
              to="/codex"
              className="inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-b from-sand-300 to-sand-500 px-7 py-3.5
                         font-display text-lg font-bold text-ocean-900 ring-1 ring-sand-100/60 shadow-lg shadow-sand-500/20
                         transition-transform hover:-translate-y-0.5 active:translate-y-0"
            >
              <BookIcon className="w-5 h-5" />
              Consulter le Codex
            </Link>
          </div>
        </div>
      </section>

      {/* Pilule flottante « Explorer le Codex » — persiste au défilement, s'efface
          quand #codex-cta entre à l'écran. */}
      <CodexExplorerFab />
    </>
  )
}

/* ---- "ways to play" — copy + hand-drawn inline SVG icons (gold line work) ---- */
const FEATURES = [
  {
    title: 'Bâtis ton île',
    text: 'Mines, scierie, distillerie, chantier naval : fais prospérer ton repaire.',
    icon: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 text-sand-300" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 24c3 0 3-1.6 6-1.6S12 24 15 24s3-1.6 6-1.6S24 24 27 24" />
        <path d="M7 22V13l6-4 6 4v9" />
        <path d="M11 22v-4h4v4" />
        <path d="M22 12V6m0 0l4 2-4 2" />
      </svg>
    ),
  },
  {
    title: 'Compose ta flotte',
    text: 'Bricks rapides, frégates, galions de ligne. Chaque coque a son rôle au combat.',
    icon: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 text-sand-300" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 21h22l-3 5H8z" />
        <path d="M16 21V4" />
        <path d="M16 6c4 .5 7 2.5 8 6-3 .5-5.5.5-8 0" />
        <path d="M16 6c-4 .5-7 2.5-8 6 3 .5 5.5.5 8 0" />
      </svg>
    ),
  },
  {
    title: 'Commerce ou pille',
    text: 'Négocie dans les comptoirs… ou prends de force. À toi de choisir ta voie.',
    icon: (
      <svg viewBox="0 0 32 32" className="w-8 h-8" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="12" r="6" stroke="#E5CC8F" strokeWidth="1.6" />
        <path d="M11 9v6M9.2 10.2h3.2a1.4 1.4 0 0 1 0 2.8H9.6a1.4 1.4 0 0 0 0 2.8h3.2" stroke="#E5CC8F" strokeWidth="1.3" />
        <path d="M19 26l8-12" stroke="#7A3E1F" strokeWidth="1.8" />
        <path d="M27 14l1.4-2.6M27 14l-2.6 1.4" stroke="#7A3E1F" strokeWidth="1.8" />
        <path d="M19 26l-2.4-.4-.4-2.4" stroke="#7A3E1F" strokeWidth="1.8" />
      </svg>
    ),
  },
  {
    title: 'Forge ta réputation',
    text: 'Marine, Guilde, Corsaire ou Pirate : tes actes (pas un menu) décident de ton pavillon.',
    icon: (
      <svg viewBox="0 0 32 32" className="w-8 h-8 text-sand-300" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 28V4" />
        <path d="M9 6h14l-3.5 4.5L23 15H9" />
        <circle cx="9" cy="4" r="1.4" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
]

const LANDING_CSS = `
  html{ scroll-behavior:smooth; }
  @media (prefers-reduced-motion: reduce){ html{ scroll-behavior:auto; } }
  .ink-title{
    background:linear-gradient(180deg,#F8F0DA 0%,#E5CC8F 52%,#C9A55C 100%);
    -webkit-background-clip:text; background-clip:text; color:transparent;
    text-shadow:0 2px 30px rgba(229,204,143,.18);
  }
  .rope-rule{ height:2px; background-image:repeating-linear-gradient(90deg,#C9A55C 0 6px,transparent 6px 12px); opacity:.5; }
  @keyframes ctaGlow { 0%,100%{ box-shadow:0 18px 40px -12px rgba(229,204,143,.45) } 50%{ box-shadow:0 0 34px 2px rgba(229,204,143,.30),0 18px 46px -10px rgba(229,204,143,.55) } }
  @keyframes hintBob { 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(4px) } }
  .cta-glow{ animation:ctaGlow 4.5s ease-in-out infinite; }
  .codex-fab-arrow{ animation:hintBob 2.2s ease-in-out infinite; }
  a:focus-visible{ outline:2px solid #E5CC8F; outline-offset:3px; border-radius:6px; }
  @media (prefers-reduced-motion: reduce){ .cta-glow,.codex-fab-arrow{ animation:none !important; } }
`
